import type { Lang } from '../../lib/i18n'

// Screen-local dictionary — kept alongside the feature, mirroring features/board/board-i18n.ts
// so this bench stays inside files_locked.
const EN = {
  title: 'Staff',
  loading: 'Loading staff…',
  errorLoading: "Couldn't load staff.",
  retry: 'Retry',
  empty: 'No staff accounts.',

  colName: 'Name',
  colUsername: 'Username',
  colRoles: 'Roles',
  colActive: 'Status',
  badgeActive: 'Active',
  badgeInactive: 'Inactive',

  actionReset: 'Reset password',
  fieldNewPassword: 'New password',
  fieldConfirmPassword: 'Confirm new password',
  submitReset: 'Reset password',
  submitting: 'Resetting…',
  cancel: 'Cancel',
  signOutEverywhere: 'Sign out all devices',
  signOutEverywhereConfirm: 'Sign this person out of every device? They sign in again with their password.',
  signOutEverywhereDone: 'Signed out of all devices.',
  signOutEverywhereFailed: 'Could not sign out the devices. Try again.',

  needsConnection: 'This action needs a connection.',
  resetSuccess: 'Password reset for {name}. That account is now signed out everywhere and signs in with the new password.',

  errorTooShort: 'Password must be at least 8 characters.',
  errorMismatch: "Passwords don't match.",
  error400: 'The new password was refused.',
  error403: 'You are not allowed to reset this password.',
  error404: 'This staff account could not be found.',
  errorOffline: 'This action needs a connection.',
  errorOther: 'Something went wrong. Try again.',

  actionSwitchOff: 'Switch off',
  actionSwitchOn: 'Switch on',
  switchingOff: 'Switching off…',
  switchingOn: 'Switching on…',
  confirmSwitchOff: 'Switch off {name}? They are signed out everywhere at once.',
  confirmSwitch: 'Confirm',

  // bench-STAFF-LOGIN-OFF-AND-OWN-PASSWORD.CB-B §1.8/§1.9 — AccountScreen.tsx and the
  // CockpitScreen Account link, kept here (not a new i18n file) so this bench stays inside
  // files_locked.
  accountNavLink: 'Account',
  accountTitle: 'Account',
  accountFieldCurrent: 'Current password',
  accountFieldNew: 'New password',
  accountFieldConfirm: 'Confirm new password',
  accountSubmit: 'Change password',
  accountSubmitting: 'Changing…',
  accountSuccess: 'Password changed. You are signed out everywhere — sign in again with the new password.',
  accountError403: 'The current password is wrong.',
  accountError429: 'Too many attempts. Try again later.',
  accountErrorOffline: 'This action needs a connection.',
  accountErrorOther: 'Something went wrong. Try again.',
  mustChangeTitle: 'Choose your own password',
  mustChangeBody: 'The owner set your password. Choose your own before you continue.',
  createOpen: 'Add a staff login',
  createTitle: 'New staff login',
  fieldName: 'Name',
  fieldUsername: 'Username',
  fieldStartPassword: 'Starting password',
  fieldRoles: 'Roles',
  createHint: 'They choose their own password the first time they sign in.',
  submitCreate: 'Create login',
  creating: 'Creating…',
  createSuccess: 'Login created for {name}.',
  errorUsernameTaken: 'That username is already taken.',
  errorNoRole: 'Pick at least one role.',
  errorRolesLoad: "Couldn't load the roles.",
  actionEditRoles: 'Change roles',
  submitRoles: 'Save roles',
  savingRoles: 'Saving…',
  rolesSaved: 'Roles saved for {name}. They are signed out everywhere and sign in again.',
  role_owner: 'Owner',
  role_hop: 'HOP',
  role_kitchen: 'Kitchen',
  role_pos: 'Point of Sale',
  role_driver: 'Driver',
  role_accountant: 'Accountant',
  role_catalog_editor: 'Catalog editor',
  role_viewer: 'Viewer',
  role_dietitian: 'Dietitian',
}

const AR: typeof EN = {
  title: 'الموظفون',
  loading: 'جارٍ تحميل الموظفين…',
  errorLoading: 'تعذّر تحميل الموظفين.',
  retry: 'إعادة المحاولة',
  empty: 'لا توجد حسابات موظفين.',

  colName: 'الاسم',
  colUsername: 'اسم المستخدم',
  colRoles: 'الأدوار',
  colActive: 'الحالة',
  badgeActive: 'نشط',
  badgeInactive: 'غير نشط',

  actionReset: 'إعادة تعيين كلمة المرور',
  fieldNewPassword: 'كلمة المرور الجديدة',
  fieldConfirmPassword: 'تأكيد كلمة المرور الجديدة',
  submitReset: 'إعادة تعيين كلمة المرور',
  submitting: 'جارٍ إعادة التعيين…',
  cancel: 'إلغاء',
  signOutEverywhere: 'تسجيل الخروج من كل الأجهزة',
  signOutEverywhereConfirm: 'تسجيل خروج هذا الشخص من كل الأجهزة؟ يسجّل الدخول مجدداً بكلمة المرور.',
  signOutEverywhereDone: 'تم تسجيل الخروج من كل الأجهزة.',
  signOutEverywhereFailed: 'تعذّر تسجيل الخروج من الأجهزة. حاول مرة ثانية.',

  needsConnection: 'هذا الإجراء يحتاج إلى اتصال بالإنترنت.',
  resetSuccess: 'تمت إعادة تعيين كلمة المرور لـ {name}. أُنهيت كل الجلسات المفتوحة لهذا الحساب، والدخول الآن بكلمة المرور الجديدة.',

  errorTooShort: 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.',
  errorMismatch: 'كلمتا المرور غير متطابقتين.',
  error400: 'تم رفض كلمة المرور الجديدة.',
  error403: 'غير مصرّح لك بإعادة تعيين كلمة مرور هذا الحساب.',
  error404: 'تعذّر العثور على حساب هذا الموظف.',
  errorOffline: 'هذا الإجراء يحتاج إلى اتصال بالإنترنت.',
  errorOther: 'حدث خطأ ما. حاول مرة أخرى.',

  actionSwitchOff: 'إيقاف الحساب',
  actionSwitchOn: 'تفعيل الحساب',
  switchingOff: 'جارٍ الإيقاف…',
  switchingOn: 'جارٍ التفعيل…',
  confirmSwitchOff: 'إيقاف حساب {name}؟ يُسجَّل خروج هذا الحساب من كل الأجهزة فورًا.',
  confirmSwitch: 'تأكيد',

  accountNavLink: 'الحساب',
  accountTitle: 'الحساب',
  accountFieldCurrent: 'كلمة المرور الحالية',
  accountFieldNew: 'كلمة المرور الجديدة',
  accountFieldConfirm: 'تأكيد كلمة المرور الجديدة',
  accountSubmit: 'تغيير كلمة المرور',
  accountSubmitting: 'جارٍ التغيير…',
  accountSuccess: 'تم تغيير كلمة المرور. تم تسجيل خروجك من كل الأجهزة، سجّل الدخول مرة أخرى بكلمة المرور الجديدة.',
  accountError403: 'كلمة المرور الحالية غير صحيحة.',
  accountError429: 'محاولات كثيرة جدًا. حاول مرة أخرى لاحقًا.',
  accountErrorOffline: 'هذا الإجراء يحتاج إلى اتصال بالإنترنت.',
  accountErrorOther: 'حدث خطأ ما. حاول مرة أخرى.',
  mustChangeTitle: 'اختر كلمة مرور خاصة بك',
  mustChangeBody: 'المالك حدّد كلمة مرورك. اختر كلمة مرور خاصة بك قبل المتابعة.',
  createOpen: 'إضافة حساب موظف',
  createTitle: 'حساب موظف جديد',
  fieldName: 'الاسم',
  fieldUsername: 'اسم المستخدم',
  fieldStartPassword: 'كلمة المرور الأولى',
  fieldRoles: 'الأدوار',
  createHint: 'يختار الموظف كلمة مروره الخاصة عند أول تسجيل دخول.',
  submitCreate: 'إنشاء الحساب',
  creating: 'جارٍ الإنشاء…',
  createSuccess: 'تم إنشاء حساب {name}.',
  errorUsernameTaken: 'اسم المستخدم هذا مستخدم من قبل.',
  errorNoRole: 'اختر دورًا واحدًا على الأقل.',
  errorRolesLoad: 'تعذّر تحميل الأدوار.',
  actionEditRoles: 'تغيير الأدوار',
  submitRoles: 'حفظ الأدوار',
  savingRoles: 'جارٍ الحفظ…',
  rolesSaved: 'تم حفظ أدوار {name}. سيُسجَّل خروجه من كل الأجهزة ويسجّل الدخول من جديد.',
  role_owner: 'المالك',
  role_hop: 'HOP',
  role_kitchen: 'المطبخ',
  role_pos: 'نقطة البيع',
  role_driver: 'السائق',
  role_accountant: 'المحاسب',
  role_catalog_editor: 'محرّر الكتالوج',
  role_viewer: 'مشاهد',
  role_dietitian: 'أخصائي التغذية',
}

const STAFF_DICT = { en: EN, ar: AR } as const

export function st<K extends keyof typeof EN>(lang: Lang, key: K): (typeof EN)[K] {
  return STAFF_DICT[lang][key]
}
